import React, { Suspense } from "react";
import { Routes, Navigate, useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { useAuth, withAuthenticationRequired } from "react-oidc-context";
import { useDispatch } from "react-redux";
import { setAutofillSchoolCode } from "../redux/SchoolSlice";
import { useTranslation } from "react-i18next";
import ChunkErrorBoundary from "./ChunkErrorBoundary/ChunkErrorBoundary";

export const suspense = (comp) => (
  <ChunkErrorBoundary>
    <Suspense fallback={<></>}>{comp}</Suspense>
  </ChunkErrorBoundary>
);
export const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);
export const localeRedirects = ["/", "/projects", "/school"];

const shouldPersistPostLoginReturn = (pathname, locale) => {
  if (!pathname || pathname === "/") return false;
  if (pathname !== `/${locale}` && !pathname.startsWith(`/${locale}/`))
    return false;
  return true;
};

export const RequiresLogin = (Component) => {
  const PrivateComponent = withAuthenticationRequired(Component);

  const Wrapper = (props) => {
    const { i18n } = useTranslation();
    const { isAuthenticated, isLoading } = useAuth();
    const locale = i18n.language;
    const dispatch = useDispatch();

    const { pathname: location } = useLocation();

    // todo: Don't duplicate route matching logic here.
    const schoolProjectDirectLinkPattern = new RegExp(
      `^/${locale}/school/project/\\w+-\\w+-\\w+$`,
    );
    const schoolClassDirectLinkPattern = new RegExp(
      `^/${locale}/school/(\\d{2}-\\d{2}-\\d{2})/class/(\\d{2}-\\d{2}-\\d{2})$`,
    );
    const schoolDirectLinkPattern = new RegExp(
      `^/${locale}/school/(\\d{2}-\\d{2}-\\d{2})$`,
    );
    const isSchoolProjectDirectLink =
      schoolProjectDirectLinkPattern.test(location);
    const schoolClassMatch = schoolClassDirectLinkPattern.exec(location);
    const isSchoolClassDirectLink = Boolean(schoolClassMatch);
    const schoolMatch = schoolDirectLinkPattern.exec(location);
    const isSchoolDirectLink = Boolean(schoolMatch);
    if (
      !isLoading &&
      !isAuthenticated &&
      (isSchoolProjectDirectLink ||
        isSchoolClassDirectLink ||
        isSchoolDirectLink)
    ) {
      const schoolCode = schoolClassMatch?.[1] || schoolMatch?.[1];
      if (schoolCode) {
        dispatch(setAutofillSchoolCode(schoolCode));
      }
      localStorage.setItem("location", location);
      return <Navigate replace to={`/${locale}/login`} />;
    }

    // Preserve the return path through auth on the current domain only.
    // Since localStorage is not shared between editor and classroom domains,
    // this is used after navigation to classroom (or any protected URL) so an
    // unauthenticated user can return to the same page after logging in.
    // Only locale-prefixed paths (matching i18n.language) are stored — e.g.
    // /auth/* is excluded because it does not match /{locale}/...
    if (
      !isLoading &&
      !isAuthenticated &&
      !localStorage.getItem("location") &&
      shouldPersistPostLoginReturn(location, locale)
    ) {
      localStorage.setItem("location", location);
    }

    return <PrivateComponent {...props} />;
  };

  return Wrapper;
};
